import { useEffect, useState } from "react";
import PropTypes from "prop-types";

const TemplateChooserModal = ({ open, onClose, templates, onContinue }) => {
  const [useTemplate, setUseTemplate] = useState(null); // "yes" | "no" | null
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    if (open) {
      setUseTemplate(null);
      setSelectedTemplateId("");
    }
  }, [open]);

  if (!open) return null;

  const canContinue =
    useTemplate === "no" || (useTemplate === "yes" && !!selectedTemplateId);

  const handleContinue = () => {
    if (!canContinue) return;
    const selected =
      useTemplate === "yes"
        ? templates.find((t) => t._id === selectedTemplateId) || null
        : null;
    onContinue(selected);
  };

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalStyle = {
    width: "560px",
    maxWidth: "90%",
    backgroundColor: "hsl(var(--card))",
    borderRadius: "12px",
    padding: "28px",
    position: "relative",
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
  };

  const titleStyle = {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "hsl(var(--foreground))",
  };

  const subtitleStyle = {
    margin: "6px 0 0 0",
    fontSize: "14px",
    color: "hsl(var(--muted-foreground))",
  };

  const closeStyle = {
    position: "absolute",
    top: "16px",
    right: "20px",
    fontSize: "18px",
    cursor: "pointer",
    color: "hsl(var(--muted-foreground))",
    userSelect: "none",
  };

  const buttonRowStyle = {
    display: "flex",
    gap: "16px",
    marginTop: "20px",
  };

  const choiceBtnStyle = (active) => ({
    flex: 1,
    padding: "14px 20px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: active ? "hsl(var(--foreground))" : "hsl(var(--card))",
    color: active ? "hsl(var(--background))" : "hsl(var(--foreground))",
    border: active ? "1px solid hsl(var(--foreground))" : "1px solid hsl(var(--border))",
  });

  const selectLabelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: 600,
    color: "hsl(var(--foreground))",
    marginBottom: "6px",
    marginTop: "20px",
  };

  const selectStyle = {
    width: "100%",
    height: "44px",
    padding: "0 12px",
    fontSize: "14px",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    backgroundColor: "hsl(var(--card))",
    outline: "none",
    cursor: "pointer",
    color: "hsl(var(--foreground))",
  };

  const continueBtnStyle = {
    width: "100%",
    marginTop: "24px",
    padding: "14px",
    backgroundColor: "hsl(var(--foreground))",
    color: "hsl(var(--background))",
    border: "none",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "14px",
    opacity: canContinue ? 1 : 0.55,
    cursor: canContinue ? "pointer" : "not-allowed",
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Create new workspace</h2>
        <p style={subtitleStyle}>Do you want to create a workspace from a template?</p>
        <span style={closeStyle} onClick={onClose}>
          X
        </span>

        <div style={buttonRowStyle}>
          <button
            type="button"
            style={choiceBtnStyle(useTemplate === "yes")}
            onClick={() => setUseTemplate("yes")}
          >
            Yes
          </button>
          <button
            type="button"
            style={choiceBtnStyle(useTemplate === "no")}
            onClick={() => setUseTemplate("no")}
          >
            No
          </button>
        </div>

        {useTemplate === "yes" && (
          <div>
            <label style={selectLabelStyle}>Select a template</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select a template</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.template_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          disabled={!canContinue}
          onClick={handleContinue}
          style={continueBtnStyle}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

TemplateChooserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templates: PropTypes.array,
  onContinue: PropTypes.func.isRequired,
};

TemplateChooserModal.defaultProps = {
  templates: [],
};

export default TemplateChooserModal;
